// Public one-time timesheet link page — no auth required, token is the credential
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ScheduledJob {
  schedule_id: number;
  job_id: number;
  job_title: string;
  site_address: string;
  start_time?: string;
  end_time?: string;
}

interface Line {
  job_id: number | null;
  description: string;
  hours: string;
}

type PageState = 'loading' | 'ready' | 'expired' | 'already_done' | 'submitted' | 'error';

export function TimesheetMagicPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [username, setUsername] = useState('');
  const [workDate, setWorkDate] = useState('');
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJob[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [lunchDeducted, setLunchDeducted] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) { setState('expired'); return; }
    // Guard (Erik 2026-08-06): if THIS browser already holds a staff session,
    // do NOT exchange — the auto-login would overwrite the admin's tokens and
    // kick them out of the admin centre (it did). Staff who open a worker's
    // link get the standalone form instead, session intact.
    try {
      const t = localStorage.getItem('accessToken');
      if (t) {
        const payload = JSON.parse(atob(t.split('.')[1] || '')) as { role?: string };
        if (['owner', 'admin', 'pm'].includes(String(payload.role))) { loadForm(); return; }
      }
    } catch { /* unreadable token — proceed with the exchange */ }
    // Land the worker in their PORTAL hours tab, signed in — the token doubles
    // as the credential. The standalone form stays as the fallback.
    fetch(`/api/timesheet-link/${token}/portal-session`, { method: 'POST' })
      .then(r => r.json())
      .then(async d => {
        if (d?.success && d.data?.accessToken) {
          const { saveTokens } = await import('@/lib/utils/tokenStorage');
          saveTokens(d.data.accessToken, d.data.refreshToken);
          // Full-page load so the portal auth context boots from the new tokens
          window.location.replace('/portal/employee/timesheet');
          return;
        }
        throw new Error('exchange failed');
      })
      .catch(() => loadForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadForm = () => {
    if (!token) { setState('expired'); return; }
    fetch(`/api/timesheet-link/${token}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) {
          if (d.error === 'already_submitted') { setUsername(d.username); setState('already_done'); }
          else setState('expired');
          return;
        }
        const { username: name, work_date, scheduled_jobs, existing_entry } = d.data;
        setUsername(name);
        setWorkDate(work_date);
        setScheduledJobs(scheduled_jobs ?? []);
        if (existing_entry?.lines?.length) {
          setLines(existing_entry.lines.map((l: any) => ({
            job_id: l.job_id, description: l.description ?? '', hours: String(l.hours),
          })));
          if (existing_entry.break_minutes >= 30) setLunchDeducted(true);
        } else if (scheduled_jobs?.length) {
          setLines(scheduled_jobs.map((j: ScheduledJob) => ({ job_id: j.job_id, description: '', hours: '' })));
        } else {
          setLines([{ job_id: null, description: '', hours: '' }]);
        }
        setState('ready');
      })
      .catch(() => setState('error'));
  };

  const addLine = () => setLines(prev => [...prev, { job_id: null, description: '', hours: '' }]);
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const updateLine = (i: number, field: keyof Line, value: string | number | null) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));

  const totalHours = lines.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
  const netHours = Math.max(0, totalHours - (lunchDeducted ? 0.5 : 0));

  const submit = async () => {
    const valid = lines.every(l => l.hours && !isNaN(parseFloat(l.hours)) && parseFloat(l.hours) > 0);
    if (!valid) { setErrorMsg('Enter valid hours for each line.'); return; }
    setBusy(true);
    setErrorMsg('');
    try {
      const r = await fetch(`/api/timesheet-link/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: lines.map(l => ({
            job_id: l.job_id ?? undefined,
            description: l.description || undefined,
            hours: parseFloat(l.hours),
          })),
          lunch_deducted: lunchDeducted,
          notes: notes || undefined,
        }),
      });
      const d = await r.json();
      if (d.success) setState('submitted');
      else setErrorMsg(d.error ?? 'Submission failed. Please try again.');
    } catch { setErrorMsg('Network error. Check your connection.'); }
    finally { setBusy(false); }
  };

  const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (state === 'loading') return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-4 border-[#C77D3F] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-gray-500 text-sm">Loading timesheet…</p>
      </div>
    </div>
  );

  if (state === 'expired') return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center space-y-3">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-red-500 text-3xl">link_off</span>
        </div>
        <h1 className="text-lg font-bold text-gray-900">Link Expired</h1>
        <p className="text-sm text-gray-500">This timesheet link has expired or is no longer valid. Contact your supervisor if you need to record hours.</p>
      </div>
    </div>
  );

  if (state === 'already_done') return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center space-y-3">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-green-600 text-3xl">check_circle</span>
        </div>
        <h1 className="text-lg font-bold text-gray-900">Already Submitted</h1>
        <p className="text-sm text-gray-500">Hi {username}, your timesheet for today has already been recorded. Thanks!</p>
      </div>
    </div>
  );

  if (state === 'submitted') return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center space-y-4">
        <div className="w-14 h-14 bg-[#C77D3F]/10 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-[#C77D3F] text-3xl">task_alt</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Hours Logged!</h1>
        <p className="text-sm text-gray-500">Thanks {username} — {netHours.toFixed(2)} net hours recorded for {fmtDate(workDate)}.</p>
        {lunchDeducted && <p className="text-xs text-gray-400">30-min lunch deducted</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF6F0] py-8 px-4">
      <div className="max-w-lg mx-auto space-y-5">

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#C77D3F] text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
            <span className="material-icons text-base">schedule</span>
            OchrePoint Timesheet
          </div>
          <h1 className="text-xl font-bold text-[#2C1810]">Hi {username}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{fmtDate(workDate)}</p>
        </div>

        {/* Scheduled jobs context */}
        {scheduledJobs.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Scheduled today</p>
            <div className="space-y-2">
              {scheduledJobs.map(j => (
                <div key={j.schedule_id} className="flex items-start gap-2">
                  <span className="material-icons text-[#C77D3F] text-base mt-0.5">place</span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{j.job_title}</p>
                    {j.site_address && <p className="text-xs text-gray-500">{j.site_address}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Entry lines */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Hours by Job</p>

          {lines.map((line, i) => (
            <div key={i} className="space-y-2 border-b border-gray-100 pb-3 last:border-0 last:pb-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Job {i + 1}</span>
                {lines.length > 1 && (
                  <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-500">
                    <span className="material-icons text-base">close</span>
                  </button>
                )}
              </div>

              {/* Job selector */}
              <select
                value={line.job_id ?? ''}
                onChange={e => updateLine(i, 'job_id', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
              >
                <option value="">— Select job (optional)</option>
                {scheduledJobs.map(j => (
                  <option key={j.job_id} value={j.job_id}>{j.job_title} — {j.site_address}</option>
                ))}
              </select>

              {/* Description */}
              <input
                type="text"
                value={line.description}
                onChange={e => updateLine(i, 'description', e.target.value)}
                placeholder="Work description (framing, excavation…)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
              />

              {/* Hours */}
              <div className="flex items-center gap-2">
                <span className="material-icons text-gray-400 text-base">access_time</span>
                <input
                  type="number"
                  min="0.25"
                  max="16"
                  step="0.25"
                  value={line.hours}
                  onChange={e => updateLine(i, 'hours', e.target.value)}
                  placeholder="Hours"
                  className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
                />
                <span className="text-xs text-gray-400">hours</span>
              </div>
            </div>
          ))}

          <button onClick={addLine} className="flex items-center gap-1.5 text-sm text-[#C77D3F] font-medium hover:underline">
            <span className="material-icons text-base">add_circle_outline</span>
            Add another job
          </button>
        </div>

        {/* Lunch + notes */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setLunchDeducted(v => !v)}
              className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${lunchDeducted ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform mt-0.5 ${lunchDeducted ? 'translate-x-4.5 ml-0.5' : 'ml-0.5'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Deduct 30-min unpaid lunch</p>
              <p className="text-xs text-gray-500">Standard BC statutory minimum break</p>
            </div>
          </label>

          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Day notes (optional — weather, site access issues, etc.)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
          />
        </div>

        {/* Summary + submit */}
        <div className="bg-white rounded-xl border border-ochre-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Gross hours</span>
            <span className="font-semibold font-mono text-gray-900">{totalHours.toFixed(2)}h</span>
          </div>
          {lunchDeducted && (
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Lunch deduction</span><span>−0.50h</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2">
            <span className="text-sm font-semibold text-gray-800">Net hours submitted</span>
            <span className="text-lg font-bold text-[#C77D3F] font-mono">{netHours.toFixed(2)}h</span>
          </div>

          {errorMsg && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{errorMsg}</p>}

          <button
            onClick={submit}
            disabled={busy || totalHours === 0}
            className="w-full bg-[#C77D3F] text-white font-bold py-3.5 rounded-xl hover:bg-[#b36e35] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {busy ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="material-icons text-base">send</span>
            )}
            {busy ? 'Submitting…' : 'Submit Timesheet'}
          </button>
          <p className="text-xs text-center text-gray-400">This link is single-use and expires at midnight.</p>
        </div>

      </div>
    </div>
  );
}
